import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormGroup, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { getZipCodeValidatorByCountry } from '../../../helpers/utils';
import { AddressValidationService } from '../../../_services/address-validation.service';

@Component({
  selector: 'app-address-fields',
  templateUrl: './address-fields.component.html',
  styleUrls: ['./address-fields.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFieldsComponent),
      multi: true
    }
  ]
})
export class AddressFieldsComponent implements ControlValueAccessor {
  @Input() submitted: boolean = false;
  @Input() role: string = '';
  @Input() countryCode: string = 'FR';
  @Input() required: boolean = false;

  addressForm: FormGroup;

  private onChange = (value: any) => {};
  private onTouched = () => {};
  private postalCodeCityValidationInProgress = false;

  constructor(
    private fb: FormBuilder,
    private addressValidationService: AddressValidationService
  ) {
    this.addressForm = this.fb.group({
      address: ['', this.getAddressValidators()],
      city: ['', this.getCityValidators()],
      zip_code: ['', this.getZipValidators()]
    });

    this.addressForm.valueChanges.subscribe(value => {
      this.onChange(value);
    });
  }

  private getValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    return validators;
  }

  private getAddressValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.addressValidator());
    return validators;
  }

  private getCityValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.cityValidator());
    return validators;
  }

  private addressValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const trimmedValue = value.trim();
      
      // Check minimum length
      if (trimmedValue.length < 3) {
        return { invalidAddress: true };
      }

      // Check for valid address characters (letters including accented, numbers, spaces, common address symbols)
      const addressRegex = /^[a-zA-ZÀ-ÿ0-9\s\-.,#/()]+$/;
      if (!addressRegex.test(trimmedValue)) {
        return { invalidAddress: true };
      }

      // Check for at least one letter (address should contain some text, including accented characters)
      if (!/[a-zA-ZÀ-ÿ]/.test(trimmedValue)) {
        return { invalidAddress: true };
      }

      return null;
    };
  }

  private cityValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const trimmedValue = value.trim();
      
      // Check minimum length
      if (trimmedValue.length < 2) {
        return { invalidCity: true };
      }

      // Check for valid city characters (letters, spaces, hyphens, apostrophes)
      const cityRegex = /^[a-zA-Z\s\-']+$/;
      if (!cityRegex.test(trimmedValue)) {
        return { invalidCity: true };
      }

      // Check for at least one letter
      if (!/[a-zA-Z]/.test(trimmedValue)) {
        return { invalidCity: true };
      }

      return null;
    };
  }

  private getZipValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.zipValidator());
    return validators;
  }

  private zipValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const trimmedValue = value.trim();
      
      // First check format using the country-specific validator
      const formatValidator = getZipCodeValidatorByCountry(() => this.countryCode);
      const formatError = formatValidator(control);
      if (formatError) {
        return formatError;
      }

      // If there are existing geographic validation errors, preserve them
      const existingErrors = control.errors;
      if (existingErrors && (existingErrors['postalCodeCityMismatch'] || existingErrors['geographicError'])) {
        return existingErrors;
      }

      // Geographic validation is handled asynchronously in onFieldBlur
      // No synchronous validation needed here
      return null;
    };
  }

  // Update validators when role or country changes
  updateRole(role: string) {
    this.role = role;
    this.updateValidators();
  }

  updateCountry(countryCode: string) {
    this.countryCode = countryCode;
    this.updateValidators();
  }

  // Update validators when required changes
  ngOnChanges() {
    this.updateValidators();
  }

  private updateValidators() {
    const addressControl = this.addressForm.get('address');
    const cityControl = this.addressForm.get('city');
    const zipControl = this.addressForm.get('zip_code');

    if (addressControl) {
      addressControl.setValidators(this.getAddressValidators());
      addressControl.updateValueAndValidity();
    }

    if (cityControl) {
      cityControl.setValidators(this.getCityValidators());
      cityControl.updateValueAndValidity();
    }

    if (zipControl) {
      zipControl.setValidators(this.getZipValidators());
      zipControl.updateValueAndValidity();
    }
  }

  writeValue(value: any): void {
    if (value) {
      this.addressForm.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressForm.disable();
    } else {
      this.addressForm.enable();
    }
  }

  get addressControl() {
    return this.addressForm.get('address');
  }

  get cityControl() {
    return this.addressForm.get('city');
  }

  get zipControl() {
    return this.addressForm.get('zip_code');
  }

  /**
   * Perform geographic validation for city
   */
  validateCityGeographically(): void {
    const cityControl = this.cityControl;
    if (!cityControl || !cityControl.value || !this.countryCode) {
      return;
    }

    const city = cityControl.value.trim();
    
    this.addressValidationService.validateCity(city, this.countryCode).subscribe({
      next: (result) => {
        if (!result.isValid) {
          const currentErrors = cityControl.errors || {};
          const newErrors = {
            ...currentErrors,
            ['cityNotFound']: true,
            ['geographicError']: result.error 
          };
          
          // Set errors and mark as invalid
          cityControl.setErrors(newErrors);
          cityControl.markAsTouched();
          
          // Force the control to be invalid
          (cityControl as any)._status = 'INVALID';
          (cityControl as any)._errors = newErrors;
        } else {
          // Remove geographic validation errors
          const currentErrors = cityControl.errors || {};
          const newErrors = { ...currentErrors };
          delete newErrors['cityNotFound'];
          delete newErrors['geographicError'];
          const finalErrors = Object.keys(newErrors).length > 0 ? newErrors : null;
          cityControl.setErrors(finalErrors);
          cityControl.markAsTouched();
        }
        
        // Update validity without triggering validators
        cityControl.updateValueAndValidity({ emitEvent: false });
      },
      error: (error) => {
        cityControl.setErrors({ 
          ...cityControl.errors, 
          ['cityNotFound']: true,
          ['geographicError']: 'Unable to validate city' 
        });
        cityControl.updateValueAndValidity();
      }
    });
  }

  /**
   * Perform geographic validation for address
   */
  validateAddressGeographically(): void {
    const addressControl = this.addressControl;
    const cityControl = this.cityControl;
    
    if (!addressControl || !addressControl.value || !cityControl || !cityControl.value || !this.countryCode) {
      return;
    }

    const address = addressControl.value.trim();
    const city = cityControl.value.trim();
    
    this.addressValidationService.validateAddress(address, city, this.countryCode).subscribe({
      next: (result) => {
        if (!result.isValid) {
          const currentErrors = addressControl.errors || {};
          const newErrors = {
            ...currentErrors,
            ['addressNotFound']: true,
            ['geographicError']: result.error 
          };
          
          // Set errors and mark as invalid
          addressControl.setErrors(newErrors);
          addressControl.markAsTouched();
          
          // Force the control to be invalid
          (addressControl as any)._status = 'INVALID';
          (addressControl as any)._errors = newErrors;
        } else {
          // Remove geographic validation errors
          const currentErrors = addressControl.errors || {};
          const newErrors = { ...currentErrors };
          delete newErrors['addressNotFound'];
          delete newErrors['geographicError'];
          const finalErrors = Object.keys(newErrors).length > 0 ? newErrors : null;
          addressControl.setErrors(finalErrors);
          addressControl.markAsTouched();
        }
        
        // Update validity without triggering validators
        addressControl.updateValueAndValidity({ emitEvent: false });
      },
      error: (error) => {
        addressControl.setErrors({ 
          ...addressControl.errors, 
          ['addressNotFound']: true,
          ['geographicError']: 'Unable to validate address' 
        });
        addressControl.updateValueAndValidity();
      }
    });
  }

  /**
   * Validate postal code and city consistency
   */
  validatePostalCodeCityConsistency(): void {
    const zipControl = this.zipControl;
    const cityControl = this.cityControl;
    
    if (!zipControl || !this.countryCode) {
      return;
    }

    const postalCode = zipControl.value?.trim();
    const city = cityControl?.value?.trim() || '';
    
    // Ne valider que si le code postal est rempli
    if (!postalCode) {
      // Si le code postal est vide, supprimer l'erreur de cohérence
      if (zipControl.errors?.['postalCodeCityMismatch']) {
        const currentErrors = { ...zipControl.errors };
        delete currentErrors['postalCodeCityMismatch'];
        delete currentErrors['geographicError'];
        const finalErrors = Object.keys(currentErrors).length > 0 ? currentErrors : null;
        zipControl.setErrors(finalErrors, { emitEvent: false });
        zipControl.updateValueAndValidity({ emitEvent: false });
      }
      return;
    }

    // Éviter les validations multiples simultanées
    if (this.postalCodeCityValidationInProgress) {
      return;
    }

    this.postalCodeCityValidationInProgress = true;

    // this.addressValidationService
    //   .validatePostalCodeCityConsistency(postalCode, city, this.countryCode)
    //   .subscribe({
    //     next: (result: any) => {
    //       this.postalCodeCityValidationInProgress = false;
          
    //       if (!result.isValid) {
    //         const currentErrors = zipControl.errors || {};
    //         const newErrors = {
    //           ...currentErrors,
    //           ['postalCodeCityMismatch']: true,
    //           ['geographicError']: result.error || 'Le code postal ne correspond pas à la ville indiquée'
    //         };
            
    //         // Set errors and mark as invalid
    //         zipControl.setErrors(newErrors, { emitEvent: false });
    //         zipControl.markAsTouched();
            
    //         // Force the control to be invalid
    //         (zipControl as any)._status = 'INVALID';
    //         (zipControl as any)._errors = newErrors;
    //       } else {
    //         // Remove postal code city mismatch errors
    //         const currentErrors = zipControl.errors || {};
    //         const newErrors = { ...currentErrors };
    //         delete newErrors['postalCodeCityMismatch'];
    //         if (newErrors['geographicError'] && typeof newErrors['geographicError'] === 'string' && newErrors['geographicError'].includes('code postal')) {
    //           delete newErrors['geographicError'];
    //         }
    //         const finalErrors = Object.keys(newErrors).length > 0 ? newErrors : null;
    //         zipControl.setErrors(finalErrors, { emitEvent: false });
    //       }
          
    //       zipControl.updateValueAndValidity({ emitEvent: false });
    //     },
    //     error: (error: any) => {
    //       this.postalCodeCityValidationInProgress = false;
    //       console.warn('Erreur lors de la validation code postal/ville:', error);
    //     }
    //   });
  }

  /**
   * Trigger geographic validation when form values change
   */
  onFieldBlur(fieldName: string): void {
    if (fieldName === 'city') {
      // Validate city when it loses focus
      setTimeout(() => {
        // this.validateCityGeographically();
        // Also validate postal code/city consistency if postal code is filled
        if (this.zipControl?.value) {
          this.validatePostalCodeCityConsistency();
        }
      }, 100);
    } else if (fieldName === 'address') {
      // Validate address when it loses focus (after city is validated)
      setTimeout(() => this.validateAddressGeographically(), 100);
    } else if (fieldName === 'zip_code') {
      // Validate postal code/city consistency when postal code loses focus
      // Validate even without city to check if postal code exists for the country
      setTimeout(() => {
        this.validatePostalCodeCityConsistency();
      }, 100);
    }
  }

  /**
   * Force validation of all address fields
   */
  forceValidation(): void {
    // Mark all controls as touched
    this.addressControl?.markAsTouched();
    this.cityControl?.markAsTouched();
    this.zipControl?.markAsTouched();
    
    // Update validity
    this.addressControl?.updateValueAndValidity();
    this.cityControl?.updateValueAndValidity();
    this.zipControl?.updateValueAndValidity();
    
    // Validate postal code/city consistency if postal code is filled
    // Validate even without city to check if postal code exists for the country
    if (this.zipControl?.value) {
      this.validatePostalCodeCityConsistency();
    }
    
    // Update the entire form
    this.addressForm?.updateValueAndValidity();
  }
}
